import React from 'react';
import {
    TextInput,
    SelectInput,
    NumberInput,
    BooleanInput,
    SelectArrayInput,
    AutocompleteArrayInput,
    ReferenceArrayInput,
    FormDataConsumer,
    required,
    minValue,
    maxValue,
    useTranslate,
    useRecordContext,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Alert,
    Chip,
} from '@mui/material';
import {
    Info,
    Schedule,
    AttachMoney,
    PeopleAlt,
    BadgeOutlined,
} from '@mui/icons-material';

const SectionHeader: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ mr: 2, color: '#00C853', display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {label}
        </Typography>
    </Box>
);

export const PolicyForm: React.FC<{ mode: 'create' | 'edit' }> = ({ mode }) => {
    const translate = useTranslate();
    const record = useRecordContext();
    const isSystemDefault = !!record?.isSystemDefault;
    // Only seed defaults when creating — in edit, the record's value should
    // win (or display empty), so users never see a misleading fallback.
    const dv = <T,>(value: T): T | undefined => (mode === 'create' ? value : undefined);

    const reservationModeChoices = [
        { id: 'fixed_slot', name: translate('resources.reservation-policies.modes.fixed_slot') },
        { id: 'next_slot', name: translate('resources.reservation-policies.modes.next_slot') },
    ];

    const accessModeChoices = [
        { id: 'reservation_only', name: translate('resources.reservation-policies.accessModes.reservation_only') },
        { id: 'reserve_or_fcfs', name: translate('resources.reservation-policies.accessModes.reserve_or_fcfs') },
    ];

    const feePolicyChoices = [
        { id: 'none', name: translate('resources.reservation-policies.feePolicies.none') },
        { id: 'hold_release', name: translate('resources.reservation-policies.feePolicies.hold_release') },
        { id: 'hold_apply', name: translate('resources.reservation-policies.feePolicies.hold_apply') },
        { id: 'hold_forfeit_on_noshow', name: translate('resources.reservation-policies.feePolicies.hold_forfeit_on_noshow') },
    ];

    const membershipLevelChoices = [
        { id: 'standard', name: translate('resources.reservation-policies.membershipLevels.standard') },
        { id: 'silver', name: translate('resources.reservation-policies.membershipLevels.silver') },
        { id: 'gold', name: translate('resources.reservation-policies.membershipLevels.gold') },
        { id: 'platinum', name: translate('resources.reservation-policies.membershipLevels.platinum') },
        { id: 'vip', name: translate('resources.reservation-policies.membershipLevels.vip') },
    ];

    return (
        <Box sx={{ width: '100%', maxWidth: 900, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
            <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                {translate('resources.reservation-policies.banners.presetLibrary')}
            </Alert>

            {/* Section 1: Identity */}
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                    <SectionHeader icon={<BadgeOutlined />} label={translate('resources.reservation-policies.sections.identity')} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {mode === 'edit' && isSystemDefault && (
                            <Box>
                                <Chip
                                    size="small"
                                    color="warning"
                                    label={translate('resources.reservation-policies.fields.isSystemDefault')}
                                />
                            </Box>
                        )}
                        <TextInput
                            source="name"
                            label={translate('resources.reservation-policies.fields.name')}
                            validate={[required()]}
                            disabled={mode === 'edit' && isSystemDefault}
                            helperText={
                                mode === 'edit' && isSystemDefault
                                    ? translate('resources.reservation-policies.helpers.systemDefaultName')
                                    : translate('resources.reservation-policies.helpers.name')
                            }
                            fullWidth
                        />
                        <TextInput
                            source="description"
                            label={translate('resources.reservation-policies.fields.description')}
                            multiline
                            minRows={2}
                            fullWidth
                            helperText={translate('resources.reservation-policies.helpers.description')}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Section 2: Mode */}
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                    <SectionHeader icon={<Info />} label={translate('resources.reservation-policies.sections.timing')} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <SelectInput
                                    source="reservationMode"
                                    label={translate('resources.reservation-policies.fields.reservationMode')}
                                    choices={reservationModeChoices}
                                    validate={[required()]}
                                    defaultValue={dv("fixed_slot")}
                                    fullWidth
                                    helperText={translate('resources.reservation-policies.helpers.reservationMode')}
                                />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                {/* Fixed-slot policies are reservation-only at the UI
                                    level: a walk-in (FCFS) could start just before a
                                    reserved slot and overrun into it, which we can't
                                    resolve gracefully while reservations are pinned to a
                                    specific charger. The backend still permits the combo,
                                    so re-enabling it here later is a UI-only change. */}
                                <FormDataConsumer<{ reservationMode?: string }>>
                                    {({ formData }) => {
                                        const isFixedSlot = (formData?.reservationMode ?? 'fixed_slot') === 'fixed_slot';
                                        return (
                                            <SelectInput
                                                source="accessMode"
                                                label={translate('resources.reservation-policies.fields.accessMode')}
                                                choices={isFixedSlot
                                                    ? accessModeChoices.filter(c => c.id === 'reservation_only')
                                                    : accessModeChoices}
                                                validate={[required()]}
                                                defaultValue={dv(isFixedSlot ? "reservation_only" : "reserve_or_fcfs")}
                                                fullWidth
                                                helperText={isFixedSlot
                                                    ? translate('resources.reservation-policies.helpers.accessModeFixedSlot')
                                                    : translate('resources.reservation-policies.helpers.accessMode')}
                                            />
                                        );
                                    }}
                                </FormDataConsumer>
                            </Box>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Section 3: Timing */}
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                    <SectionHeader icon={<Schedule />} label={translate('resources.reservation-policies.sections.timingWindows')} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormDataConsumer<{ reservationMode?: string; slotGranularityMinutes?: number | null }>>
                                    {({ formData }) => {
                                        const isNextSlot = formData?.reservationMode === 'next_slot';
                                        const granularity = formData?.slotGranularityMinutes;
                                        const divisibility = (value: number | null | undefined): string | undefined => {
                                            if (value == null) return undefined;
                                            if (granularity == null || granularity <= 0) return undefined;
                                            return Number(value) % Number(granularity) === 0
                                                ? undefined
                                                : translate('resources.reservation-policies.validation.granularityInvalid');
                                        };
                                        return (
                                            <NumberInput
                                                source="slotDurationMinutes"
                                                label={translate('resources.reservation-policies.fields.slotDurationMinutes')}
                                                validate={isNextSlot ? [] : [required(), minValue(15), maxValue(720), divisibility]}
                                                defaultValue={dv(60)}
                                                disabled={isNextSlot}
                                                fullWidth
                                                helperText={translate(
                                                    isNextSlot
                                                        ? 'resources.reservation-policies.helpers.slotDurationDisabled'
                                                        : 'resources.reservation-policies.helpers.slotDuration'
                                                )}
                                            />
                                        );
                                    }}
                                </FormDataConsumer>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <NumberInput
                                    source="slotGranularityMinutes"
                                    label={translate('resources.reservation-policies.fields.slotGranularityMinutes')}
                                    validate={[required(), minValue(5), maxValue(120)]}
                                    defaultValue={dv(30)}
                                    fullWidth
                                    helperText={translate('resources.reservation-policies.helpers.slotGranularity')}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <NumberInput
                                    source="holdWindowMinutes"
                                    label={translate('resources.reservation-policies.fields.holdWindowMinutes')}
                                    validate={[required(), minValue(5), maxValue(120)]}
                                    defaultValue={dv(15)}
                                    fullWidth
                                    helperText={translate('resources.reservation-policies.helpers.holdWindow')}
                                />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormDataConsumer<{ reservationMode?: string }>>
                                    {({ formData }) => {
                                        const isNextSlot = formData?.reservationMode === 'next_slot';
                                        return (
                                            <NumberInput
                                                source="leadTimeMinutes"
                                                label={translate('resources.reservation-policies.fields.leadTimeMinutes')}
                                                validate={isNextSlot ? [] : [minValue(0), maxValue(1440)]}
                                                defaultValue={dv(15)}
                                                disabled={isNextSlot}
                                                fullWidth
                                                helperText={translate(
                                                    isNextSlot
                                                        ? 'resources.reservation-policies.helpers.naNextSlot'
                                                        : 'resources.reservation-policies.helpers.leadTime'
                                                )}
                                            />
                                        );
                                    }}
                                </FormDataConsumer>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormDataConsumer<{ reservationMode?: string }>>
                                    {({ formData }) => {
                                        const isNextSlot = formData?.reservationMode === 'next_slot';
                                        return (
                                            <NumberInput
                                                source="cancelWindowMinutes"
                                                label={translate('resources.reservation-policies.fields.cancelWindowMinutes')}
                                                validate={isNextSlot ? [] : [minValue(0), maxValue(1440)]}
                                                defaultValue={dv(30)}
                                                disabled={isNextSlot}
                                                fullWidth
                                                helperText={translate(
                                                    isNextSlot
                                                        ? 'resources.reservation-policies.helpers.naNextSlot'
                                                        : 'resources.reservation-policies.helpers.cancelWindow'
                                                )}
                                            />
                                        );
                                    }}
                                </FormDataConsumer>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormDataConsumer<{ reservationMode?: string }>>
                                    {({ formData }) => {
                                        const isNextSlot = formData?.reservationMode === 'next_slot';
                                        return (
                                            <NumberInput
                                                source="modifyWindowMinutes"
                                                label={translate('resources.reservation-policies.fields.modifyWindowMinutes')}
                                                validate={isNextSlot ? [] : [minValue(0), maxValue(1440)]}
                                                defaultValue={dv(30)}
                                                disabled={isNextSlot}
                                                fullWidth
                                                helperText={translate(
                                                    isNextSlot
                                                        ? 'resources.reservation-policies.helpers.naNextSlot'
                                                        : 'resources.reservation-policies.helpers.modifyWindow'
                                                )}
                                            />
                                        );
                                    }}
                                </FormDataConsumer>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormDataConsumer<{ reservationMode?: string }>>
                                    {({ formData }) => {
                                        const isNextSlot = formData?.reservationMode === 'next_slot';
                                        return (
                                            <NumberInput
                                                source="maxAdvanceDays"
                                                label={translate('resources.reservation-policies.fields.maxAdvanceDays')}
                                                validate={isNextSlot ? [] : [minValue(1), maxValue(90)]}
                                                defaultValue={dv(7)}
                                                disabled={isNextSlot}
                                                fullWidth
                                                helperText={translate(
                                                    isNextSlot
                                                        ? 'resources.reservation-policies.helpers.naNextSlot'
                                                        : 'resources.reservation-policies.helpers.maxAdvanceDays'
                                                )}
                                            />
                                        );
                                    }}
                                </FormDataConsumer>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <NumberInput
                                    source="maxActivePerUser"
                                    label={translate('resources.reservation-policies.fields.maxActivePerUser')}
                                    validate={[minValue(1), maxValue(20)]}
                                    defaultValue={dv(3)}
                                    fullWidth
                                    helperText={translate('resources.reservation-policies.helpers.maxActivePerUser')}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormDataConsumer<{ reservationMode?: string }>>
                                    {({ formData }) => {
                                        const isNextSlot = formData?.reservationMode === 'next_slot';
                                        return (
                                            <NumberInput
                                                source="maxModifications"
                                                label={translate('resources.reservation-policies.fields.maxModifications')}
                                                validate={isNextSlot ? [] : [minValue(0), maxValue(10)]}
                                                defaultValue={dv(2)}
                                                disabled={isNextSlot}
                                                fullWidth
                                                helperText={translate(
                                                    isNextSlot
                                                        ? 'resources.reservation-policies.helpers.naNextSlot'
                                                        : 'resources.reservation-policies.helpers.maxModifications'
                                                )}
                                            />
                                        );
                                    }}
                                </FormDataConsumer>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <NumberInput
                                    source="liveStatusHorizonHours"
                                    label={translate('resources.reservation-policies.fields.liveStatusHorizonHours')}
                                    validate={[minValue(0), maxValue(168)]}
                                    defaultValue={dv(2)}
                                    fullWidth
                                    helperText={translate('resources.reservation-policies.helpers.liveStatusHorizonHours')}
                                />
                            </Box>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Section 4: Fee Behavior */}
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                    <SectionHeader icon={<AttachMoney />} label={translate('resources.reservation-policies.sections.feeBehavior')} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Alert severity="info">
                            {translate('resources.reservation-policies.banners.feeBehavior')}
                        </Alert>
                        <SelectInput
                            source="feePolicy"
                            label={translate('resources.reservation-policies.fields.feePolicy')}
                            choices={feePolicyChoices}
                            validate={[required()]}
                            defaultValue={dv("none")}
                            fullWidth
                            helperText={translate('resources.reservation-policies.helpers.feePolicy')}
                        />
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormDataConsumer<{ reservationMode?: string }>>
                                    {({ formData }) => {
                                        const isNextSlot = formData?.reservationMode === 'next_slot';
                                        return (
                                            <NumberInput
                                                source="blockedNoshowToleranceMinutes"
                                                label={translate('resources.reservation-policies.fields.blockedNoshowToleranceMinutes')}
                                                validate={isNextSlot ? [] : [required(), minValue(0), maxValue(120)]}
                                                defaultValue={dv(5)}
                                                disabled={isNextSlot}
                                                fullWidth
                                                helperText={translate(
                                                    isNextSlot
                                                        ? 'resources.reservation-policies.helpers.naNextSlot'
                                                        : 'resources.reservation-policies.helpers.blockedNoshowTolerance'
                                                )}
                                            />
                                        );
                                    }}
                                </FormDataConsumer>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                                <FormDataConsumer<{ reservationMode?: string }>>
                                    {({ formData }) => {
                                        const isNextSlot = formData?.reservationMode === 'next_slot';
                                        return (
                                            <BooleanInput
                                                source="forfeitOnBlockedNoShow"
                                                label={translate('resources.reservation-policies.fields.forfeitOnBlockedNoShow')}
                                                defaultValue={dv(false)}
                                                disabled={isNextSlot}
                                                helperText={translate(
                                                    isNextSlot
                                                        ? 'resources.reservation-policies.helpers.naNextSlot'
                                                        : 'resources.reservation-policies.helpers.forfeitOnBlockedNoShow'
                                                )}
                                            />
                                        );
                                    }}
                                </FormDataConsumer>
                            </Box>
                        </Box>
                        <FormDataConsumer<{ reservationMode?: string }>>
                            {({ formData }) => {
                                const isNextSlot = formData?.reservationMode === 'next_slot';
                                return (
                                    <NumberInput
                                        source="blockedDetectionIncrementMinutes"
                                        label={translate('resources.reservation-policies.fields.blockedDetectionIncrementMinutes')}
                                        validate={isNextSlot ? [] : [required(), minValue(0), maxValue(60)]}
                                        defaultValue={dv(1)}
                                        disabled={isNextSlot}
                                        fullWidth
                                        helperText={translate(
                                            isNextSlot
                                                ? 'resources.reservation-policies.helpers.naNextSlot'
                                                : 'resources.reservation-policies.helpers.blockedDetectionIncrement'
                                        )}
                                    />
                                );
                            }}
                        </FormDataConsumer>
                    </Box>
                </CardContent>
            </Card>

            {/* Section 5: Eligibility */}
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                    <SectionHeader icon={<PeopleAlt />} label={translate('resources.reservation-policies.sections.eligibility')} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <SelectArrayInput
                            source="allowedMembershipLevels"
                            label={translate('resources.reservation-policies.fields.allowedMembershipLevels')}
                            choices={membershipLevelChoices}
                            fullWidth
                            helperText={translate('resources.reservation-policies.helpers.allowedMembershipLevels')}
                        />
                        <ReferenceArrayInput
                            source="allowedGroupIds"
                            reference="user-groups"
                            perPage={500}
                        >
                            <AutocompleteArrayInput
                                optionText="name"
                                fullWidth
                                label={translate('resources.reservation-policies.fields.allowedGroupIds')}
                                helperText={translate('resources.reservation-policies.helpers.allowedGroupIds')}
                            />
                        </ReferenceArrayInput>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default PolicyForm;
